import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { EmptyRow, LoadingOverlay, MultiSelect, RECEIVABLES_API, idList, useReceivableLookups } from "./common";

/**
 * Customers who have reached the limits set in Receivable Settings (legacy RecDashboard cards): bounced cheques at or over
 * "Maximum Bounce Cheques Allowed" and open invoices at or over "Maximum Outstanding Invoices Allowed". A list stays empty
 * while its maximum is 0.
 */
const CustomerWatchlist = () => {
  const { salesmen } = useReceivableLookups(["salesmen"]);
  const [salesmanIds, setSalesmanIds] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await api.get(`${RECEIVABLES_API}/collections/limit-watch`, { salesmanUserIds: idList(salesmanIds) }));
    } catch (error) {
      toast.error(error.message || "Failed to load the watchlist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesmanIds]);

  const bounced = data?.bouncedCheques || [];
  const outstanding = data?.outstandingInvoices || [];

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="filters-bar">
        <MultiSelect
          options={salesmen.map((s) => ({ value: s.userId, label: s.name }))}
          selected={salesmanIds}
          onChange={setSalesmanIds}
          placeholder="All salespersons"
        />
      </div>

      <h3 style={{ fontWeight: 600, margin: "22px 0 4px" }}>Cheque Bounce</h3>
      <div className="page-subtitle" style={{ marginBottom: 8 }}>
        {data?.maxChequeBouncesAllowed
          ? `Customers with ${data.maxChequeBouncesAllowed} or more bounced cheques`
          : "Set Maximum Bounce Cheques Allowed in Settings to use this list"}
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Salesperson</th>
              <th style={{ textAlign: "center" }}>Bounced Cheques</th>
              <th>Cheque Numbers</th>
            </tr>
          </thead>
          <tbody>
            {bounced.length === 0 ? (
              <EmptyRow colSpan={4} />
            ) : (
              bounced.map((row) => (
                <tr key={row.customerId}>
                  <td>{row.customerName}</td>
                  <td>{row.salesmanName || "-"}</td>
                  <td style={{ textAlign: "center" }}>{row.bouncedCheques}</td>
                  <td>{row.chequeNumbers}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <h3 style={{ fontWeight: 600, margin: "22px 0 4px" }}>Outstanding Invoices</h3>
      <div className="page-subtitle" style={{ marginBottom: 8 }}>
        {data?.maxOutstandingInvoicesAllowed
          ? `Customers with ${data.maxOutstandingInvoicesAllowed} or more open invoices`
          : "Set Maximum Outstanding Invoices Allowed in Settings to use this list"}
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Salesperson</th>
              <th>Beat</th>
              <th style={{ textAlign: "center" }}>Invoices</th>
              <th>Invoice Numbers</th>
            </tr>
          </thead>
          <tbody>
            {outstanding.length === 0 ? (
              <EmptyRow colSpan={5} />
            ) : (
              outstanding.map((row) => (
                <tr key={row.customerId}>
                  <td>{row.customerName}</td>
                  <td>{row.salesmanName || "-"}</td>
                  <td>{row.beatName || "-"}</td>
                  <td style={{ textAlign: "center" }}>{row.invoices}</td>
                  <td>{row.invoiceNumbers}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomerWatchlist;
